import { Brand } from '@awaze/design-system.components.awaze-theme-provider'
import { LocaleFormatter } from '@awaze/mfe-common.locale-formatting'
import { IncomingHttpHeaders } from 'http'
import React, { useContext } from 'react'

import { SupportedBrand } from '../lib/properties/properties.types'

const brands: Brand[] = [
  'cottages.com',
  'hoseasons',
  'agnostic',
  'awaze',
  'dansommer',
  'novasol',
  'fincallorca',
  'jamesVillas',
]

const countries = [
  'EU',
  'DK',
  'AT',
  'GB',
  'DE',
  'ES',
  'FR',
  'HR',
  'IT',
  'NL',
  'NO',
  'PL',
  'SE',
  'US',
] as const
export type AwazeCountry = (typeof countries)[number]

const countryFromHeaderValue = (
  headerCountry: string | string[],
): AwazeCountry => {
  const country = Array.isArray(headerCountry)
    ? headerCountry[0]
    : headerCountry
  const u = country?.toLocaleUpperCase() as AwazeCountry
  return countries.includes(u) ? u : undefined // 'EU'
}

export const getFirstValidBrand = (
  brandHeader: string,
): SupportedBrand | undefined => {
  if (!brandHeader) {
    return undefined
  }

  const brandList = brandHeader.split(',')

  return brandList.find((brand) =>
    brands.includes(brand as SupportedBrand),
  ) as SupportedBrand
}

export const validateHeaders = (
  xHeaders: IncomingHttpHeaders,
  query?: Record<string, string | string[]>,
): {
  brand: SupportedBrand
  language: string
  country: AwazeCountry
  currency: string
  marketCode: string
  newAWS: boolean
  headersWarnings: string[]
  awazeClient: string
} => {
  const {
    'x-awaze-brand': brand,
    'x-awaze-currency': headerCurrency,
    'x-awaze-market-code': headerMarketCode,
    'x-awaze-aws': headerNewAws,
    'x-awaze-client': headerClient,
  } = xHeaders

  let { 'x-awaze-language': headerLanguage, 'x-awaze-country': headerCountry } =
    xHeaders

  if (
    !headerCountry &&
    !headerLanguage &&
    query?.locale &&
    typeof query.locale === 'string'
  ) {
    const [language, country] = query.locale.split('-')
    if (language) {
      headerLanguage = language
      if (country) {
        headerCountry = country
      } else {
        // TODO: this is maybe a bit strange of a default...
        headerCountry = 'DK'
      }
    }
  }

  const language = Array.isArray(headerLanguage)
    ? headerLanguage[0]
    : headerLanguage
  const currency = Array.isArray(headerCurrency)
    ? headerCurrency[0]
    : headerCurrency
  const marketCode = Array.isArray(headerMarketCode)
    ? headerMarketCode[0]
    : headerMarketCode
  const newAWS = Array.isArray(headerNewAws) ? headerNewAws[0] : headerNewAws
  const isNewAWS = newAWS === 'new'

  const hasWarnings = []
  const allowedBrand = getFirstValidBrand(
    (brand || query?.brand) as SupportedBrand,
  )
  if (!allowedBrand && process.env.NEXT_PUBLIC_API_MOCKING !== 'enabled') {
    hasWarnings.push(
      `Request contained an invalid header x-awaze-brand: ${brand}`,
    )
  }

  const allowedCountry = countryFromHeaderValue(headerCountry)
  if (!allowedCountry && process.env.NEXT_PUBLIC_API_MOCKING !== 'enabled') {
    hasWarnings.push(
      `Request contained an invalid header x-awaze-country: ${headerCountry}`,
    )
  }

  return {
    brand: allowedBrand ?? 'cottages.com',
    language: language ?? 'en',
    country: allowedCountry ?? 'EU',
    currency: currency ?? 'EUR',
    marketCode: marketCode ?? '826',
    newAWS: isNewAWS ?? false,
    headersWarnings: hasWarnings,
    awazeClient: typeof headerClient === 'string' ? headerClient : '',
  }
}

export interface GlobalPropsInterface {
  brand: Brand
  contentfulLocale: string
  marketCode: string
  country: AwazeCountry
  currency: string
  slug: string
  optimizelySdkKey: string
  experiments: Record<string, string> | null
  pageType: string | null
  host: string
  awazeClient: string
}

export const GlobalProps: React.Context<GlobalPropsInterface> =
  React.createContext({
    brand: 'cottages.com',
    contentfulLocale: 'en',
    marketCode: '826', // default to "826" English.
    country: 'EU',
    currency: 'EUR',
    slug: '/',
    optimizelySdkKey: '',
    experiments: null,
    pageType: null,
    host: '',
    awazeClient: '',
  })

export const useFormatCurrency = (): LocaleFormatter => {
  const { contentfulLocale: locale, currency } = useContext(GlobalProps)
  return new LocaleFormatter(locale, currency)
}
